import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { REV_BASIC } from './index';
import {
  useDelete1,
  useDeleteFile1,
  useDeleteActivity1,
  useSave1,
  useSaveActivity3,
  useSaveFile2,
  useSearch1,
  useSearch2,
  useSearch3,
  useSearch4,
} from './REV_BASIC_QUERY';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Button,
  Stack,
  Paper,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import ComboBasic from '../../../../Components/MUIs/ComboBasic';
import { Delete as DeleteIcon } from '@mui/icons-material';
import useFileUpload from '../../../../hooks/useFileUpload';
import Commons from '../../../../Services/Common/Common';
import Global from '../../../../Services/Common/Global';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import { apiRequest } from '../../../../Services/Common/ApiRequest';
import * as XLSX from 'xlsx';
import {
  buildUploadFileLikeDialog,
  normalizePreviewFilePath,
} from './revBasicFileUpload';

const getInitials = (name) => {
  const n = String(name ?? '').trim();
  if (!n) return '?';
  const parts = n.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return n.substring(0, 2).toUpperCase();
};

function REV_BASICF() {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((s) => s.BaseInfo);
  const inlineFileInputRef = useRef(null);
  const createFileInputRef = useRef(null);

  const [vendorFilterText, setVendorFilterText] = useState('');
  const [activeTabId, setActiveTabId] = useState('all');
  const [gridQuickFilter, setGridQuickFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const todayStr = useMemo(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  }, []);

  const threeMonthsAgoStr = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 3);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  }, []);

  const [startCreateDate, setStartCreateDate] = useState(threeMonthsAgoStr);
  const [endCreateDate, setEndCreateDate] = useState(todayStr);

  const [vendors, setVendors] = useState([]);
  const [activeVendorId, setActiveVendorId] = useState('');

  const [allRows, setAllRows] = useState([]);
  const [rows, setRows] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [inlineDraft, setInlineDraft] = useState(null);
  /** FileUploadDialogNoGrid와 동일 업로드 대기열 */
  const [inlinePendingFiles, setInlinePendingFiles] = useState([]);
  const [createPendingFiles, setCreatePendingFiles] = useState([]);
  const [activityComment, setActivityComment] = useState('');
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [versionLogOpen, setVersionLogOpen] = useState(false);
  const [versionLogRows, setVersionLogRows] = useState([]);
  const [versionLogLoading, setVersionLogLoading] = useState(false);

  const assigneeComboItems = useMemo(() => {
    const opts = (assigneeOptions ?? []).map((o) => ({
      name: String(o.assigneeNm ?? o.assigneeId ?? ''),
      value: String(o.assigneeId ?? ''),
    }));
    return [{ name: '(미지정)', value: '' }, ...opts];
  }, [assigneeOptions]);

  const { refetch: SearchBadges } = useSearch1({
    onSuccess: async (badgeRows) => {
      const mapped = (badgeRows ?? []).map((r) => ({
        id: r.coCd,
        name: r.coNm,
        initials: getInitials(r.coNm),
        badge: Number(r.requirementCount ?? 0),
        coId: r.coId,
        address1: r.address1,
        address2: r.address2,
        contractStartDate: r.contractStartDate,
        contractEndDate: r.contractEndDate,
      }));
      setVendors(mapped);
      setActiveVendorId((prev) => prev || mapped?.[0]?.id || '');
    },
  });

  const { refetch: SearchAssignees } = useSearch4({
    onSuccess: async (rows) => {
      const mapped = (rows ?? []).map((r) => ({
        assigneeId: r.assigneeId,
        assigneeNm: r.assigneeNm,
      }));
      setAssigneeOptions(mapped);
    },
  });

  const assigneeNameById = useMemo(() => {
    const m = new Map();
    (assigneeOptions ?? []).forEach((o) => {
      const id = String(o?.assigneeId ?? '').trim();
      if (!id) return;
      m.set(id, String(o?.assigneeNm ?? '').trim());
    });
    return m;
  }, [assigneeOptions]);

  const { run: SearchList } = useSearch2({
    onSuccess: async (listRows) => {
      const mapped = (listRows ?? []).map((r, idx) => ({
        reqSeq: r.reqSeq,
        coCd: r.coCd,
        title: r.title,
        content: r.content,
        category: r.category,
        priority: r.priorityLvl,
        status: r.status,
        assigneeId: r.assigneeId,
        assigneeNm:
          assigneeNameById.get(String(r.assigneeId ?? '').trim()) ?? '',
        dueDate: r.dueDate ? String(r.dueDate).substring(0, 10) : '',
        _agGridRowId: `rev-${idx}-${String(r.reqSeq ?? '')}`,
      }));

      setAllRows(mapped);
    },
  });

  // 담당자 목록이 나중에 로드되는 케이스 대비: 기존 allRows에 이름 주입
  useEffect(() => {
    if (!assigneeOptions || assigneeOptions.length === 0) return;
    setAllRows((prev) =>
      (prev ?? []).map((r) => {
        const id = String(r?.assigneeId ?? '').trim();
        if (!id) return { ...r, assigneeNm: '' };
        const nm = assigneeNameById.get(id) ?? '';
        if (String(r?.assigneeNm ?? '') === nm) return r;
        return { ...r, assigneeNm: nm };
      })
    );
  }, [assigneeNameById, assigneeOptions]);

  const buildR2Context = (overrides = {}) => ({
    CoCd: activeVendorId,
    Category:
      activeTabId === 'web'
        ? 'Web'
        : activeTabId === 'mobile'
          ? 'Mobile'
          : activeTabId === 'common'
            ? 'Common'
            : '',
    Status: statusFilter === 'all' ? '' : statusFilter,
    SearchText: gridQuickFilter,
    StartDate: startCreateDate,
    EndDate: endCreateDate,
    ...overrides,
  });

  useEffect(() => {
    const filtered = (allRows ?? []).filter((x) => {
      const categoryOk =
        activeTabId === 'all' ||
        (activeTabId === 'web' && (x.category ?? '') === 'Web') ||
        (activeTabId === 'mobile' && (x.category ?? '') === 'Mobile') ||
        (activeTabId === 'common' && (x.category ?? '') === 'Common');

      const statusOk =
        statusFilter === 'all' || String(x.status ?? '') === String(statusFilter);

      return categoryOk && statusOk;
    });

    const priorityRank = (p) => {
      const v = String(p ?? '').toUpperCase();
      if (v === 'P1') return 1;
      if (v === 'P2') return 2;
      if (v === 'P3') return 3;
      return 99;
    };

    const isDone = (s) => {
      const v = String(s ?? '');
      return v === '완료' || v === '배포완료';
    };

    const sorted = [...filtered].sort((a, b) => {
      // 1) 완료/배포완료는 맨 아래
      const doneA = isDone(a?.status);
      const doneB = isDone(b?.status);
      if (doneA !== doneB) return doneA ? 1 : -1;

      // 2) 우선순위(P1 -> P2 -> P3)
      const prA = priorityRank(a?.priority);
      const prB = priorityRank(b?.priority);
      if (prA !== prB) return prA - prB;

      // 3) 마감일 오름차순(없으면 뒤로)
      const dueA = String(a?.dueDate ?? '').substring(0, 10);
      const dueB = String(b?.dueDate ?? '').substring(0, 10);
      const hasA = Boolean(dueA);
      const hasB = Boolean(dueB);
      if (hasA !== hasB) return hasA ? -1 : 1;
      if (dueA && dueB && dueA !== dueB) return dueA.localeCompare(dueB);

      // 4) 마지막 tie-breaker
      return Number(a?.reqSeq ?? 0) - Number(b?.reqSeq ?? 0);
    });

    setRows(sorted);
    setSelectedRow((prev) => {
      if (prev && sorted.some((r) => r.reqSeq === prev.reqSeq)) return prev;
      return sorted?.[0] ?? null;
    });
  }, [allRows, activeTabId, statusFilter]);

  const { run: SearchDetail } = useSearch3({
    onSuccess: async (detailRows) => {
      const first = (detailRows ?? [])?.[0] ?? null;
      const req = first?.requirement ?? null;
      const files = first?.files ?? [];
      const activities = first?.activities ?? [];
      setSelectedDetail({
        requirement: req,
        files,
        activities,
      });
    },
  });

  const { runAsync: SaveReqAsync } = useSave1();
  const { runAsync: SaveFileAsync } = useSaveFile2();
  const { runAsync: SaveActivityAsync } = useSaveActivity3();
  const { runAsync: DeleteActivityAsync } = useDeleteActivity1();
  const { runAsync: DeleteFileAsync } = useDeleteFile1();
  const { runAsync: DeleteReqAsync } = useDelete1();
  const { SaveLogicPreview, SaveLogicFile, DeleteLogicFile } = useFileUpload({
    oldFilePathField: 'filePath',
    newFileNameField: 'saveName',
    getTargetPath: (item) => `RequirementFiles/${String(item.reqSeq ?? '')}`,
  });

  const downloadAttachment = async (fileName, rawPath) => {
    try {
      let filePath = String(rawPath ?? '').replace(/\\/g, '/');
      if (!filePath) {
        dispatch(
          showSnackbar({
            message: '다운로드할 파일 경로가 없습니다.',
            type: 'warning',
          })
        );
        return;
      }
      filePath = filePath
        .replace(`${Global.file.useUrl}files/`, '')
        .replace(/^\/?files\//, '');
      const finalFileName =
        fileName && fileName !== 'downloaded_file'
          ? fileName
          : decodeURIComponent(filePath.split('/').pop() || 'downloaded_file');

      const result = await Commons.Axios(
        'fileDown',
        Global.file.useUrl + Global.urls.Execute.UrlFileDownload,
        { FileName: finalFileName, Url: Commons.EncodeUrl(filePath) }
      );

      if (!result?.data || !result?.headers?.['content-type']) {
        dispatch(
          showSnackbar({
            message: `"${finalFileName}" 다운로드 응답이 비정상입니다.`,
            type: 'error',
          })
        );
        return;
      }

      const blob = new Blob([result.data], {
        type: result.headers['content-type'],
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = finalFileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      dispatch(
        showSnackbar({
          message: `"${finalFileName}" 다운로드 완료`,
          type: 'success',
        })
      );
    } catch (error) {
      dispatch(
        showSnackbar({
          message: error?.message || '다운로드 중 오류가 발생했습니다.',
          type: 'error',
        })
      );
    }
  };

  const addPendingUpload = async (file, scope) => {
    if (!file) return;
    if (file.name.length > 100) {
      dispatch(
        showSnackbar({
          message:
            '파일명 길이가 100자를 초과합니다. 100자 이하로 파일명을 수정해 주세요.',
          type: 'warning',
        })
      );
      return;
    }

    const { fileToSave, saveName, displayName } = buildUploadFileLikeDialog(file);
    const previewPath = await SaveLogicPreview(fileToSave);
    if (!previewPath) return;

    const normalized =
      normalizePreviewFilePath(previewPath) || '';
    if (!normalized) return;

    const clientId =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `tmp-${Date.now()}-${Math.random()}`;

    const item = {
      clientId,
      fileName: displayName,
      saveName,
      previewFilePath: normalized,
    };

    if (scope === 'inline') {
      setInlinePendingFiles((p) => [...p, item]);
    } else {
      setCreatePendingFiles((p) => [...p, item]);
    }

    dispatch(
      showSnackbar({
        message: `파일이 선택되었습니다: ${displayName}`,
        type: 'success',
      })
    );
  };

  const deleteServerAttachment = async (f) => {
    const fp = String(f?.filePath ?? '').trim();
    const fileSeq = Number(f?.fileSeq ?? 0);
    const reqSeq = Number(inlineDraft?.reqSeq ?? selectedRow?.reqSeq ?? 0);
    if (!fileSeq || !reqSeq) return;

    try {
      if (fp) {
        const delPhys = await DeleteLogicFile(fp);
        if (delPhys == null) {
          return;
        }
      }
      await DeleteFileAsync([{ reqSeq, fileSeq }]);
      dispatch(
        showSnackbar({ message: '첨부파일이 삭제되었습니다.', type: 'success' })
      );
      SearchDetail({ ReqSeq: reqSeq });
    } catch (e) {
      dispatch(
        showSnackbar({
          message: e?.message || '첨부파일 삭제에 실패했습니다.',
          type: 'error',
        })
      );
    }
  };

  useEffect(() => {
    SearchBadges();
    SearchAssignees();
  }, []);

  useEffect(() => {
    if (!activeVendorId) return;
    const t = setTimeout(() => {
      SearchList(buildR2Context());
    }, 300);
    return () => clearTimeout(t);
  }, [
    activeVendorId,
    activeTabId,
    statusFilter,
    gridQuickFilter,
    startCreateDate,
    endCreateDate,
  ]);

  useEffect(() => {
    if (!selectedRow?.reqSeq) {
      setSelectedDetail(null);
      setInlineDraft(null);
      setInlinePendingFiles([]);
      return;
    }
    setInlineDraft({
      reqSeq: Number(selectedRow.reqSeq ?? 0),
      coCd: String(selectedRow.coCd ?? activeVendorId ?? ''),
      title: String(selectedRow.title ?? ''),
      content: String(selectedRow.content ?? ''),
      category: String(selectedRow.category ?? ''),
      priority: String(selectedRow.priority ?? 'P2'),
      status: String(selectedRow.status ?? '시작전'),
      assigneeId: String(selectedRow.assigneeId ?? ''),
      dueDate: String(selectedRow.dueDate ?? ''),
    });
    setInlinePendingFiles([]);
    setActivityComment('');
    SearchDetail({ ReqSeq: selectedRow.reqSeq });
  }, [selectedRow?.reqSeq]);

  const tabs = useMemo(
    () => [
      { id: 'all', label: '전체 (All)' },
      { id: 'web', label: '웹 (Web)' },
      { id: 'mobile', label: '모바일 (Mobile)' },
      { id: 'common', label: '공통 (Common)' },
    ],
    []
  );

  const [createOpen, setCreateOpen] = useState(false);
  const [createDraft, setCreateDraft] = useState({
    reqSeq: 0,
    coCd: '',
    title: '',
    content: '',
    category: '',
    priority: 'P2',
    status: '시작전',
    assigneeId: '',
    dueDate: '',
  });
  const [createFiles, setCreateFiles] = useState([]);

  const createNew = async () => {
    const coCd = activeVendorId || userInfo?.coCd || '';
    setCreateDraft({
      reqSeq: 0,
      coCd,
      title: '',
      content: '',
      category: '',
      priority: 'P2',
      status: '시작전',
      assigneeId: '',
      dueDate: '',
    });
    setCreatePendingFiles([]);
    setCreateOpen(true);
  };

  const saveCreate = async () => {
    const payload = [{ ...createDraft }];
    const resp = await SaveReqAsync(payload);
    const savedReqSeq =
      resp?.data?.[0]?.reqSeq ??
      resp?.data?.[0]?.ReqSeq ??
      resp?.[0]?.reqSeq ??
      resp?.[0]?.ReqSeq ??
      createDraft.reqSeq ??
      0;

    // 첨부파일 업로드 + TB_PJ_REQ_FILES 저장 (FileUploadDialogNoGrid와 동일 흐름)
    if (createPendingFiles.length > 0 && savedReqSeq) {
      const fileMetaRows = [];
      for (const p of createPendingFiles) {
        const moveResult = await SaveLogicFile(
          [
            {
              reqSeq: savedReqSeq,
              filePath: '',
              saveName: p.saveName,
              previewFilePath: p.previewFilePath,
            },
          ],
          ''
        );
        const movedPath =
          moveResult?.[0]?.filePath ??
          moveResult?.[0]?.FilePath ??
          moveResult?.[0]?.newFilePath ??
          moveResult?.[0]?.NewFilePath ??
          '';
        if (!movedPath) continue;
        fileMetaRows.push({
          reqSeq: savedReqSeq,
          fileName: p.fileName,
          filePath: movedPath,
        });
      }
      if (fileMetaRows.length > 0) {
        await SaveFileAsync(fileMetaRows);
      }
      setCreatePendingFiles([]);
    }

    setCreateOpen(false);
    await SearchBadges();
    SearchList(buildR2Context({ SearchText: '' }));
    if (savedReqSeq) {
      setSelectedRow((prev) => ({
        ...(prev ?? {}),
        reqSeq: savedReqSeq,
        coCd: createDraft.coCd,
        title: createDraft.title,
        content: createDraft.content,
        category: createDraft.category,
        priority: createDraft.priority,
        status: createDraft.status,
        assigneeId: createDraft.assigneeId,
        dueDate: createDraft.dueDate,
      }));
    }
  };

  const saveInline = async () => {
    if (!inlineDraft?.reqSeq) return;
    const payload = [{ ...inlineDraft }];
    await SaveReqAsync(payload);

    if (inlinePendingFiles.length > 0) {
      const fileMetaRows = [];
      for (const p of inlinePendingFiles) {
        const moveResult = await SaveLogicFile(
          [
            {
              reqSeq: inlineDraft.reqSeq,
              filePath: '',
              saveName: p.saveName,
              previewFilePath: p.previewFilePath,
            },
          ],
          ''
        );
        const movedPath =
          moveResult?.[0]?.filePath ??
          moveResult?.[0]?.FilePath ??
          moveResult?.[0]?.newFilePath ??
          moveResult?.[0]?.NewFilePath ??
          '';
        if (!movedPath) continue;
        fileMetaRows.push({
          reqSeq: inlineDraft.reqSeq,
          fileName: p.fileName,
          filePath: movedPath,
        });
      }
      if (fileMetaRows.length > 0) {
        await SaveFileAsync(fileMetaRows);
      }
      setInlinePendingFiles([]);
    }

    await SearchBadges();
    SearchList(buildR2Context());
    setSelectedRow((prev) => ({
      ...(prev ?? {}),
      ...inlineDraft,
    }));
    SearchDetail({ ReqSeq: inlineDraft.reqSeq });
  };

  const deleteSelected = async () => {
    if (!selectedRow?.reqSeq) return;
    await DeleteReqAsync([{ reqSeq: selectedRow.reqSeq }]);
    await SearchBadges();
    SearchList(buildR2Context());
  };

  const deleteMyActivityComment = async (a) => {
    const reqSeq = Number(selectedRow?.reqSeq ?? 0);
    const actSeq = Number(a?.actSeq ?? 0);
    if (!reqSeq || !actSeq) return;
    try {
      await DeleteActivityAsync([{ reqSeq, actSeq }]);
      dispatch(
        showSnackbar({ message: '댓글이 삭제되었습니다.', type: 'success' })
      );
      SearchDetail({ ReqSeq: reqSeq });
    } catch (e) {
      dispatch(
        showSnackbar({
          message: e?.message || '댓글 삭제에 실패했습니다.',
          type: 'error',
        })
      );
    }
  };

  const submitActivityComment = async () => {
    const reqSeq = Number(selectedRow?.reqSeq ?? 0);
    if (!reqSeq) return;
    const text = String(activityComment ?? '').trim();
    if (!text) {
      dispatch(
        showSnackbar({
          message: '댓글 내용을 입력해 주세요.',
          type: 'warning',
        })
      );
      return;
    }
    try {
      await SaveActivityAsync([{ reqSeq, comment: text }]);
      setActivityComment('');
      dispatch(
        showSnackbar({ message: '댓글이 등록되었습니다.', type: 'success' })
      );
      SearchDetail({ ReqSeq: reqSeq });
    } catch (e) {
      dispatch(
        showSnackbar({
          message: e?.message || '댓글 등록에 실패했습니다.',
          type: 'error',
        })
      );
    }
  };

  const openVersionLog = async () => {
    setVersionLogOpen(true);
    setVersionLogLoading(true);
    try {
      const res = await apiRequest(
        'post',
        Global.urls.Utility.getVersionLog,
        {}
      );
      const rows = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      const mapped = (rows ?? []).map((r, idx) => ({
        type: r?.type ?? r?.Type ?? '',
        version: r?.version ?? r?.Version ?? '',
        _k: `ver-${idx}-${String(r?.type ?? r?.Type ?? '')}-${String(r?.version ?? r?.Version ?? '')}`,
      }));
      setVersionLogRows(mapped);
    } catch (e) {
      dispatch(
        showSnackbar({
          message: e?.message || '버전 로그 조회에 실패했습니다.',
          type: 'error',
        })
      );
      setVersionLogRows([]);
    } finally {
      setVersionLogLoading(false);
    }
  };

  const downloadExcel = async () => {
    if (!activeVendorId) return;

    try {
      const context = buildR2Context();

      // server는 encrypted parameter를 기대하므로 schema mapper와 동일하게 암호화
      const payload = {
        CoCd: Commons.EncryptAES(String(context.CoCd ?? '')),
        Category: Commons.EncryptAES(String(context.Category ?? '')),
        Status: Commons.EncryptAES(String(context.Status ?? '')),
        SearchText: Commons.EncryptAES(String(context.SearchText ?? '')),
        StartDate: Commons.EncryptAES(String(context.StartDate ?? '')),
        EndDate: Commons.EncryptAES(String(context.EndDate ?? '')),
      };

      const res = await apiRequest('post', Global.urls.Dashboard.getRevBasicR2, payload);
      const rows = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];

      const sheetRows = (rows ?? []).map((r) => ({
        ID: r.reqSeq ?? '',
        업무명: r.title ?? '',
        설명: r.content ?? '',
        카테고리: r.category ?? '',
        우선순위: r.priorityLvl ?? '',
        상태: r.status ?? '',
        담당자: r.assigneeId ?? '',
        마감일: r.dueDate ? String(r.dueDate).substring(0, 10) : '',
        등록일: r.createDate ? String(r.createDate).substring(0, 19) : '',
        수정일: r.updateDate ? String(r.updateDate).substring(0, 19) : '',
      }));

      const ws = XLSX.utils.json_to_sheet(sheetRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Requirements');

      const today = new Date();
      const y = today.getFullYear();
      const m = String(today.getMonth() + 1).padStart(2, '0');
      const d = String(today.getDate()).padStart(2, '0');
      const fileName = `REV_REQUIREMENTS_${String(activeVendorId)}_${y}${m}${d}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (e) {
      dispatch(
        showSnackbar({
          message: e?.message || '엑셀 다운로드에 실패했습니다.',
          type: 'error',
        })
      );
    }
  };

  return (
    <Box
      className="wrap"
      sx={{
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <REV_BASIC
        vendors={vendors}
        activeVendorId={activeVendorId}
        onSelectVendor={setActiveVendorId}
        vendorFilterText={vendorFilterText}
        onChangeVendorFilterText={setVendorFilterText}
        tabs={tabs}
        activeTabId={activeTabId}
        onChangeTabId={setActiveTabId}
        gridQuickFilter={gridQuickFilter}
        onChangeGridQuickFilter={setGridQuickFilter}
        statusFilter={statusFilter}
        onChangeStatusFilter={setStatusFilter}
        startCreateDate={startCreateDate}
        endCreateDate={endCreateDate}
        onChangeStartCreateDate={setStartCreateDate}
        onChangeEndCreateDate={setEndCreateDate}
        onDownloadExcel={downloadExcel}
        rows={rows}
        selectedRow={selectedRow}
        onSelectRow={setSelectedRow}
        selectedDetail={selectedDetail}
        onCreateNew={createNew}
        onDelete={deleteSelected}
        inlineDraft={inlineDraft}
        onInlineChange={(patch) =>
          setInlineDraft((p) => (p ? { ...p, ...patch } : p))
        }
        onInlineSave={saveInline}
        inlineFileInputRef={inlineFileInputRef}
        onInlineAttachChange={async (e) => {
          const file = e.target.files?.[0];
          if (e.target) e.target.value = '';
          await addPendingUpload(file, 'inline');
        }}
        inlinePendingFiles={inlinePendingFiles}
        onRemoveInlinePending={(clientId) =>
          setInlinePendingFiles((p) => p.filter((x) => x.clientId !== clientId))
        }
        onDownloadServerFile={(f) =>
          downloadAttachment(f?.fileName, f?.filePath)
        }
        onDeleteServerFile={deleteServerAttachment}
        activityComment={activityComment}
        onActivityCommentChange={setActivityComment}
        onSubmitActivityComment={submitActivityComment}
        currentUserId={userInfo?.userid ?? ''}
        onDeleteActivityComment={deleteMyActivityComment}
        assigneeComboItems={assigneeComboItems}
        onOpenVersionLog={openVersionLog}
      />

      <Dialog
        open={versionLogOpen}
        onClose={() => setVersionLogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>앱 버전 로그</DialogTitle>
        <DialogContent>
          <Paper variant="outlined" sx={{ mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 900, width: 140 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 900 }}>Version</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {versionLogLoading ? (
                  <TableRow>
                    <TableCell colSpan={2}>불러오는 중...</TableCell>
                  </TableRow>
                ) : versionLogRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2}>표시할 데이터가 없습니다.</TableCell>
                  </TableRow>
                ) : (
                  versionLogRows.map((r) => (
                    <TableRow key={r._k}>
                      <TableCell>{r.type}</TableCell>
                      <TableCell>{r.version}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVersionLogOpen(false)}>닫기</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>요구사항 추가</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="업무명"
              value={createDraft.title}
              onChange={(e) =>
                setCreateDraft((p) => ({ ...p, title: e.target.value }))
              }
              fullWidth
            />
            <TextField
              label="설명"
              value={createDraft.content}
              onChange={(e) =>
                setCreateDraft((p) => ({ ...p, content: e.target.value }))
              }
              fullWidth
              multiline
              minRows={3}
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="카테고리"
                value={createDraft.category}
                onChange={(e) =>
                  setCreateDraft((p) => ({ ...p, category: e.target.value }))
                }
                fullWidth
                select
              >
                <MenuItem value="">(없음)</MenuItem>
                <MenuItem value="Web">Web</MenuItem>
                <MenuItem value="Mobile">Mobile</MenuItem>
                <MenuItem value="Common">Common</MenuItem>
              </TextField>
              <TextField
                label="우선순위"
                value={createDraft.priority}
                onChange={(e) =>
                  setCreateDraft((p) => ({ ...p, priority: e.target.value }))
                }
                fullWidth
                select
              >
                <MenuItem value="P1">P1</MenuItem>
                <MenuItem value="P2">P2</MenuItem>
                <MenuItem value="P3">P3</MenuItem>
              </TextField>
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                label="상태"
                value={createDraft.status}
                onChange={(e) =>
                  setCreateDraft((p) => ({ ...p, status: e.target.value }))
                }
                fullWidth
                select
              >
                <MenuItem value="시작전">시작전</MenuItem>
                <MenuItem value="진행중">진행중</MenuItem>
                <MenuItem value="완료">완료</MenuItem>
                <MenuItem value="배포완료">배포완료</MenuItem>
              </TextField>
              <TextField
                label="마감일"
                type="date"
                value={createDraft.dueDate}
                onChange={(e) =>
                  setCreateDraft((p) => ({ ...p, dueDate: e.target.value }))
                }
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            <ComboBasic
              size="small"
              fullWidth
              name="담당자"
              value={createDraft.assigneeId ?? ''}
              items={assigneeComboItems}
              onChange={(v) =>
                setCreateDraft((p) => ({ ...p, assigneeId: String(v ?? '') }))
              }
              wrapSx={{ mt: 0.5 }}
            />

            <Paper elevation={1} sx={{ p: 1.5 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <TextField
                  placeholder="파일 선택"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value="파일 선택"
                  inputProps={{
                    readOnly: true,
                    tabIndex: -1,
                    style: { caretColor: 'transparent' },
                  }}
                  onClick={() => createFileInputRef.current?.click()}
                  InputLabelProps={{ shrink: true }}
                />
                <input
                  type="file"
                  ref={createFileInputRef}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (e.target) e.target.value = '';
                    await addPendingUpload(file, 'create');
                  }}
                  style={{ display: 'none' }}
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.png,.jpg,.jpeg,.hwp"
                />
              </Stack>
            </Paper>
            {createPendingFiles.length > 0 && (
              <Stack spacing={1}>
                {createPendingFiles.map((p) => (
                  <Stack
                    key={p.clientId}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                  >
                    <TextField
                      size="small"
                      fullWidth
                      value={p.fileName}
                      InputProps={{ readOnly: true }}
                    />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() =>
                        setCreatePendingFiles((list) =>
                          list.filter((x) => x.clientId !== p.clientId)
                        )
                      }
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>취소</Button>
          <Button variant="contained" onClick={saveCreate}>
            저장
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default REV_BASICF;

